<template>
  <div class="app-container">
    <el-row :gutter="20">
      <!-- 学分概览卡片 -->
      <el-col :span="16">
        <el-card class="credit-card">
          <div class="flex-header">
            <div>
              <h3 class="title">我的学分</h3>
              <p class="subtitle">{{ userInfo.majorName }} · {{ userInfo.majorYear }}</p>
            </div>
            <el-tag type="info">{{ currentMonth }}</el-tag>
          </div>
          
          <div class="credit-overview">
            <div class="credit-score">
              <span class="current-score">{{ creditOverview.currentScore }}</span>
              <span class="max-score">/{{ creditOverview.totalScore }}</span>
            </div>
            <div class="credit-change">
              <span :class="creditOverview.monthChange >= 0 ? 'increase' : 'decrease'">
                <i :class="creditOverview.monthChange >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i> 
                {{ creditOverview.monthChange >= 0 ? '+' : '' }}{{ creditOverview.monthChange }}分
              </span>
              <small>本月变化</small>
            </div>
          </div>
          
          <!-- 进度条 -->
          <div class="progress-container">
            <div class="progress-track">
              <div class="progress-value" :style="{ width: progressPercentage + '%' }"></div>
            </div>
            
            <!-- 标记线容器 -->
            <div class="markers-container">
              <div class="progress-marker graduation-label" :style="{ left: graduationLine + '%' }">
                毕业线 ({{ graduationScore }}分) <!-- 动态显示数值 -->
              </div>
              <!-- <div class="progress-marker" :style="{ left: safeLine + '%' }">
                <div class="marker-label">安全线</div>
              </div>-->
          </div>
            </div> 
          
          <div class="credit-status">
            <div>
              <el-tag :type="creditStatus.type">{{ creditStatus.text }}</el-tag>
              <span class="status-text">{{ creditStatus.detail }}</span>
            </div>
            <div class="update-time">
              <small>最近更新时间: {{ creditOverview.lastUpdate }}</small>
            </div>
          </div>
        </el-card>
        
        <!-- 学分记录表 -->
        <el-card class="mt-4 credit-card">
          <div class="table-header">
            <h3 class="title">学分记录明细</h3>
            <div class="filters-container">
              <el-select v-model="filterType" class="filter-select" placeholder="全部类型">
                <el-option label="全部类型" value="all"></el-option>
                <el-option label="加分" value="add"></el-option>
                <el-option label="扣分" value="deduct"></el-option>
              </el-select>
              <el-date-picker
                v-model="filterDate"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                class="fixed-date-picker"
                @change="handleDateFilterChange"
              ></el-date-picker>
            </div>
          </div>
          
          <el-table :data="pagedCreditLogs" style="width: 100%" v-loading="loading">
            <el-table-column prop="operateTime" label="时间" width="150">
              <template slot-scope="{row}">
                {{ formatDate(row.operateTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="changeType" label="类型" width="100">
              <template slot-scope="{row}">
                <el-tag v-if="row.changeType === '扣分'" type="danger" size="small">扣分</el-tag>
                <el-tag v-else type="success" size="small">加分</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="changeValue" label="分值变化" width="120">
              <template slot-scope="{row}">
                <span :class="row.changeType === '扣分' ? 'text-danger' : 'text-success'" class="change-value">
                  {{ row.changeType === '扣分' ? '-' : '+' }}{{ row.changeValue }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="reason" label="详细事由"></el-table-column>
            <el-table-column prop="operatorName" label="操作人" width="100"></el-table-column>
          </el-table>
          
          <el-pagination
            class="mt-4"
            background
            layout="prev, pager, next"
            :total="filteredCreditLogs.length"
            :page-size="pageSize"
            :current-page.sync="currentPage"
          ></el-pagination>
        </el-card>
      </el-col>
      
      <!-- 右侧面板 -->
      <el-col :span="8">
        <!-- 学分构成图表 -->
        <el-card class="credit-card mb-4">
          <h3 class="title">学分构成</h3>
          <div class="chart-container">
            <canvas ref="chartCanvas"></canvas>
          </div>
          
          <div class="credit-breakdown">
            <div class="breakdown-item">
              <div class="color-indicator bg-primary"></div>
              <span>基础学分</span>
              <span class="value">{{ creditBreakdown.base }}分</span>
            </div>
            <div class="breakdown-item">
              <div class="color-indicator bg-success"></div>
              <span>奖励学分</span>
              <span class="value">+{{ creditBreakdown.reward }}分</span>
            </div>
            <div class="breakdown-item">
              <div class="color-indicator bg-danger"></div>
              <span>扣减学分</span>
              <span class="value">-{{ creditBreakdown.deduction }}分</span>
            </div>
          </div>
        </el-card>
        
        <!-- 通知卡片 -->
        <el-card class="credit-card mb-4 notification-card notification-success" v-if="rewardOpportunity">
          <div class="notification-content">
            <i class="el-icon-warning text-success"></i>
            <div>
              <h4 class="notification-title">可获得的奖励机会</h4>
              <p class="notification-text">{{ rewardOpportunity }}</p>
            </div>
          </div>
        </el-card>
        
        <el-card class="credit-card notification-card notification-warning" v-if="creditWarning">
          <div class="notification-content">
            <i class="el-icon-warning-outline text-warning"></i>
            <div>
              <h4 class="notification-title">学分使用提示</h4>
              <p class="notification-text">{{ creditWarning }}</p>
            </div>
          </div>
        </el-card>
        
        <!-- 快速操作 -->
        <el-card class="credit-card mt-4">
          <h3 class="title">快速操作</h3>
          
          <el-button class="action-card" @click="handleCreditApply">
            <div class="action-content">
              <i class="el-icon-document text-primary"></i>
              <div>
                <h4 class="action-title">学分奖励申请</h4>
                <p class="action-subtitle">比赛获奖、证书报考等</p>
              </div>
            </div>
          </el-button>
          
          <el-button class="action-card" @click="handleAppeal" v-if="canAppeal">
            <div class="action-content">
              <i class="el-icon-question text-gray"></i>
              <div>
                <h4 class="action-title">学分申诉</h4>
                <p class="action-subtitle">对扣分记录有异议</p>
              </div>
            </div>
          </el-button>
          
          <el-button class="action-card" @click="showCreditRules">
            <div class="action-content">
              <i class="el-icon-notebook-2 text-info"></i>
              <div>
                <h4 class="action-title">查看学分规则</h4>
                <p class="action-subtitle">学校学分管理规定</p>
              </div>
            </div>
          </el-button>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { listStudent } from "@/api/system/student";
import { listLogs } from "@/api/system/logs";
import { getUserProfile } from "@/api/system/user";
import { listRules } from "@/api/system/rules";

export default {
  name: 'MyCredit',
  data() {
    return {
      // 用户信息
      userInfo: {
        majorName: '',
        majorYear: '',
        studentId: null
      },
      // 学分概览数据
      creditOverview: {
        currentScore: 0,
        totalScore: 100,
        monthChange: 0,
        lastUpdate: ''
      },
      // 学分构成
      creditBreakdown: {
        base: 0,
        reward: 0,
        deduction: 0
      },
      // 学分记录数据
      creditLogs: [],
      // 规则数据
      ruleOptions: [],
      // 奖励机会
      rewardOpportunity: '',
      // 学分警告
      creditWarning: '',
      // 是否可以申诉
      canAppeal: false,
      
      // 过滤器
      filterType: 'all',
      filterDate: null,
      currentPage: 1,
      pageSize: 6,
      loading: false,
      
      // 图表实例
      creditChart: null
    };
  },
  computed: {
    // 毕业线具体分数
  graduationScore() {
    // 根据是否为半年制，返回对应的毕业分数要求
    const isHalfYear = this.userInfo.majorYear && this.userInfo.majorYear.includes('半年');
    return isHalfYear ? 30 : 60; // 半年制30分，否则60分
  },
    // 当前月份
    currentMonth() {
      const now = new Date();
      return `${now.getFullYear()}年${now.getMonth() + 1}月`;
    },
    
    // 进度条百分比
    progressPercentage() {
      if (this.creditOverview.totalScore === 0) return 0;
      return (this.creditOverview.currentScore / this.creditOverview.totalScore) * 100;
    },
    
    // 毕业线位置 (60%)
    graduationLine() {
      return 60;
    },
    
    // 安全线位置 (70%)
    safeLine() {
      return 70;
    },
    
    // 学分状态
    creditStatus() {
      const percentage = this.progressPercentage;
      if (percentage >= this.safeLine) {
        return {
          type: 'success',
          text: '安全范围'
          // detail: `高于安全线${(percentage - this.safeLine).toFixed(1)}%`
        };
      } else if (percentage >= this.graduationLine) {
        return {
          type: 'warning',
          text: '需努力'
          // detail: `低于安全线${(this.safeLine - percentage).toFixed(1)}%`
        };
      } else {
        return {
          type: 'danger',
          text: '危险范围'
          // detail: `低于毕业线${(this.graduationLine - percentage).toFixed(1)}%`
        };
      }
    },
    
    // 过滤后的学分记录
    filteredCreditLogs() {
      let result = [...this.creditLogs];
      
      // 按类型过滤
      if (this.filterType !== 'all') {
        result = result.filter(item => 
          (this.filterType === 'add' && item.changeType === '加分') ||
          (this.filterType === 'deduct' && item.changeType === '扣分')
        );
      }
      
      // 按日期过滤
      if (this.filterDate && this.filterDate[0] && this.filterDate[1]) {
        const start = new Date(this.filterDate[0]);
        const end = new Date(this.filterDate[1]);
        end.setHours(23, 59, 59, 999); // 包含结束日期当天
        
        result = result.filter(item => {
          const logDate = new Date(item.operateTime);
          return logDate >= start && logDate <= end;
        });
      }
      
      return result;
    },
    
    // 分页后的学分记录
    pagedCreditLogs() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      return this.filteredCreditLogs.slice(startIndex, startIndex + this.pageSize);
    }
  },
  methods: {
    // 初始化数据
    async initData() {
      this.loading = true;
      try {
        // 获取用户信息
        const userResponse = await getUserProfile();
        console.log("用户", userResponse);
        this.userInfo = userResponse.data;
        this.userInfo.majorName = userResponse.user.majorName;
        this.userInfo.majorYear = userResponse.major.majorYear;
        this.userInfo.studentId = userResponse.user.id;

        const studentId = this.userInfo.studentId;
        
        // 根据学制设置总学分和基础学分
        const isHalfYear = this.userInfo.majorYear && this.userInfo.majorYear.includes('半年');
        this.creditOverview.totalScore = isHalfYear ? 50 : 100;
        this.creditBreakdown.base = isHalfYear ? 50 : 100;
        
        // 获取学生学分信息
        const overviewResponse = await listStudent({ studentId });
        
        if (overviewResponse.rows && overviewResponse.rows.length > 0) {
          const studentData = overviewResponse.rows[0];
          this.creditOverview.currentScore = studentData.totalCredits || 0;
          this.creditOverview.lastUpdate = studentData.lastUpdate ? new Date(studentData.lastUpdate).toLocaleString() : new Date().toLocaleString();
        } else {
          this.creditOverview.currentScore = 0;
          this.creditOverview.lastUpdate = new Date().toLocaleString();
        }
        
        // 获取学分记录
        const logsResponse = await listLogs({ studentId });
        this.creditLogs = logsResponse.rows || [];
        
        // 计算学分构成
        this.calculateCreditBreakdown();
        
        // 获取规则数据
        const rulesResponse = await listRules();
        this.ruleOptions = rulesResponse.rows || [];
        
        // 分析数据生成提示信息
        this.analyzeCreditData();
        
        // 初始化图表
        this.$nextTick(() => {
          if (this.$refs.chartCanvas) {
            this.initChart();
          }
        });
      } catch (error) {
        console.error('获取数据失败:', error);
        this.$message.error('数据加载失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 计算学分构成
    calculateCreditBreakdown() {
      // 重置数据
      this.creditBreakdown.reward = 0;
      this.creditBreakdown.deduction = 0;
      
      // 计算奖励学分和扣减学分
      this.creditLogs.forEach(log => {
        if (log.changeValue > 0) {
          this.creditBreakdown.reward += log.changeValue;
        } else if (log.changeValue < 0) {
          this.creditBreakdown.deduction += Math.abs(log.changeValue);
        }
      });
      
      // 计算本月变化（最近30天的变化）
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      let monthChange = 0;
      this.creditLogs.forEach(log => {
        if (new Date(log.operateTime) >= thirtyDaysAgo) {
          monthChange += log.changeValue;
        }
      });
      
      this.creditOverview.monthChange = monthChange;
    },
    
    // 分析学分数据生成提示
    analyzeCreditData() {
      // 分析最近30天的扣分记录
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentDeductions = this.creditLogs.filter(log => 
        log.changeType === '扣分' && new Date(log.operateTime) >= thirtyDaysAgo
      );
      
      if (recentDeductions.length >= 3) {
        this.creditWarning = `过去30天您有${recentDeductions.length}次扣分记录，请关注行为规范`;
      }
      
      // 检查是否有可申诉的扣分记录（24小时内）
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
      
      this.canAppeal = this.creditLogs.some(log => 
        log.changeType === '扣分' && 
        new Date(log.operateTime) >= twentyFourHoursAgo
      );
      
      // 生成奖励机会提示（随机或根据规则）
      const rewardRules = this.ruleOptions.filter(rule => rule.changeValue > 0);
      if (rewardRules.length > 0) {
        const randomRule = rewardRules[Math.floor(Math.random() * rewardRules.length)];
        this.rewardOpportunity = `${randomRule.ruleName}，可获得${randomRule.changeValue}分加分`;
      }
    },
    
    // 快速操作处理函数
    handleCreditApply() {
      this.$message.success('学分奖励申请功能已打开');
    },

    handleAppeal() {
      this.$message.info('学分申诉功能已打开');
    },

    showCreditRules() {
      //this.$router.push('/creditrules');
      const docUrl = '/docs/山东欧曼谛学分制12月.doc';
  window.open(docUrl, '_blank');
    },

    // 日期筛选变化
    handleDateFilterChange() {
      this.currentPage = 1;
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    },

    // 初始化图表
    initChart() {
      if (this.creditChart) {
        this.creditChart.destroy();
      }
      
      const ctx = this.$refs.chartCanvas.getContext('2d');
      this.creditChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['基础学分', '奖励学分', '扣减学分'],
          datasets: [{
            data: [this.creditBreakdown.base, this.creditBreakdown.reward, this.creditBreakdown.deduction],
            backgroundColor: ['#3a86ff', '#2ecc71', '#e74c3c'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.label}: ${context.parsed}分`;
                }
              }
            }
          },
          cutout: '70%'
        }
      });
    },
    
    // 处理窗口调整事件
    handleResize() {
      if (this.creditChart) {
        this.creditChart.destroy();
        this.$nextTick(() => {
          this.initChart();
        });
      }
    }
  },
  mounted() {
    this.initData();
    // 添加窗口调整监听器
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // 组件销毁前移除事件监听器
    window.removeEventListener('resize', this.handleResize);
    
    // 销毁图表实例
    if (this.creditChart) {
      this.creditChart.destroy();
    }
  }
};
</script>

<style scoped>
/* 基础样式 */
.app-container {
  padding: 20px;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', Arial, sans-serif;
}

/* 卡片样式 */
.credit-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: none;
  margin-bottom: 20px;
  overflow: hidden;
}

.credit-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

/* 标题样式 */
.title {
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 8px;
  color: #303133;
}

.subtitle {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

/* 头部布局 */
.flex-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* 学分概览 */
.credit-overview {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 30px;
}

.credit-score {
  display: flex;
  align-items: baseline;
}

.current-score {
  font-size: 36px;
  font-weight: bold;
  color: #3a86ff;
}

.max-score {
  font-size: 18px;
  color: #909399;
  margin-left: 5px;
}

.credit-change {
  text-align: right;
}

.increase {
  display: block;
  font-weight: 500;
  color: #2ecc71;
  font-size: 16px;
}

.decrease {
  display: block;
  font-weight: 500;
  color: #e74c3c;
  font-size: 16px;
}

/* 进度条 */
.progress-container {
  margin: 25px 0;
  position: relative;
  height: 60px; /* 增加高度给标记线空间 */
}

.progress-track {
  height: 24px;
  border-radius: 12px;
  background: #f0f2f5;
  overflow: visible; /* 改为visible允许标记线显示 */
  position: relative;
  z-index: 1;
}

.progress-value {
  height: 100%;
  background: linear-gradient(90deg, #3a86ff, #4e9fff);
  border-radius: 12px;
  position: absolute;
  left: 0;
  top: 0;
  transition: width 0.6s ease;
  z-index: 2;
}

/* 标记线容器 */
.markers-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
}

.progress-marker {
  position: absolute;
  top: 0;
  height: 24px;
  width: 3px;
  background: #ff6b6b;
  z-index: 3;
}

/* 使用三角形箭头替代黑线 */
.progress-marker::after {
  content: '';
  position: absolute;
  top: -8px;
  left: -4px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 8px solid #ff6b6b;
}

.marker-label {
  position: absolute;
  top: -30px;
  font-size: 12px;
  white-space: nowrap;
  transform: translateX(-50%);
  color: #ff6b6b;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* 学分状态 */
.credit-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #eee;
}

/* 学分记录明细头部布局 */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap; /* 允许换行 */
  margin-bottom: 20px;
  display: flex;

flex-direction: column;

align-items: flex-start;

}

.filters-container {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap; /* 允许换行 */
  margin-top:20px;
}

.filter-select {
  width: 120px;
}
.graduation-label {
  font-size: 11px; /* 或者您喜欢的更小尺寸，如10px、11px、12px */
}

/* 修复日期选择器样式 */
.fixed-date-picker {
  width: 100%;
  min-width: 420px;
}

.fixed-date-picker .el-range-separator {
  width: 60px !important;
  padding: 0 8px !important;
  color: #606266;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fixed-date-picker .el-range-input {
  width: 42% !important;
  text-align: center;
}

.fixed-date-picker .el-range__close-icon {
  display: none;
}

/* 学分变化值 */
.change-value {
  font-weight: bold;
}

.text-success {
  color: #2ecc71;
}

.text-danger {
  color: #e74c3c;
}

/* 学分构成 */
.credit-breakdown {
  margin-top: 20px;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.color-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

.value {
  font-weight: 500;
}

.bg-primary {
  background-color: #3a86ff;
}

.bg-success {
  background-color: #2ecc71;
}

.bg-danger {
  background-color: #e74c3c;
}

/* 通知卡片 */
.notification-card {
  border-left: 4px solid;
  padding: 15px;
}

.notification-content {
  display: flex;
  align-items: flex-start;
}

.notification-content i {
  font-size: 20px;
  margin-right: 12px;
  margin-top: 3px;
}

.notification-title {
  font-weight: bold;
  font-size: 15px;
  margin-bottom: 4px;
  color: #303133;
}

.notification-text {
  font-size: 13px;
  color: #606266;
  margin: 0;
}

.notification-success {
  border-left-color: #2ecc71;
  background-color: rgba(46, 204, 113, 0.05);
}

.notification-warning {
  border-left-color: #f39c12;
  background-color: rgba(243, 156, 18, 0.05);
}

/* 操作按钮 */
.action-card {
  display: flex;
  width: 100%;
  text-align: left;
  padding: 12px 15px;
  margin-bottom: 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #ebeef5;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: all 0.2s ease;
}

.action-card:hover {
  background-color: #f5f7fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.08);
}

.action-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.action-content i {
  font-size: 24px;
  margin-right: 12px;
  flex-shrink: 0;
}

.action-title {
  font-weight: bold;
  font-size: 15px;
  margin-bottom: 2px;
  color: #303133;
}

.action-subtitle {
  font-size: 12px;
  color: #909399;
  margin: 0;
}

.text-primary {
  color: #3a86ff;
}

.text-info {
  color: #3a86ff;
}

.text-gray {
  color: #909399;
}

/* 图表容器 */
.chart-container {
  position: relative;
  height: 250px;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .el-col {
    width: 100%;
  }
  
  .el-col-16, .el-col-8 {
    max-width: 100%;
    flex: 0 0 100%;
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filters-container {
    width: 100%;
    margin-top: 10px;
  }
  
  .filter-select {
    width: 40%;
    margin-right: 10px;
  }
  
  .date-picker {
    width: 60%;
  }
}

@media (max-width: 768px) {
  .filters-container {
    flex-direction: column;
  }
  
  .filter-select, .date-picker {
    width: 100%;
    margin-bottom: 10px;
  }
}
</style>